// Shared IP Advisor (IP Expert) status groupings.
//
// These groups are the single source of truth shared between the IP Advisor
// dashboard stat cards (IpExpertDashboard) and the All Assessments page filter
// (AllAssessments). Keeping them here guarantees a card's count always matches
// the rows shown when that card is clicked through to All Assessments.
//
// Status strings are the `actionItem` labels produced by the backend
// `getStats` controller (see eip-server/controllers/experts/memberDetails.ts).

export interface IpeStatusGroup {
  id: string;
  label: string;
  statuses: string[];
}

export const IPE_STATUS_GROUPS: IpeStatusGroup[] = [
  { id: "requests", label: "IP Assessment Requests", statuses: ["New Assessment Request", "Invite Sent"] },
  { id: "inProgress", label: "IP Assessment In Progress", statuses: ["Ongoing Assessment"] },
  { id: "awaitingReview", label: "SoWs Awaiting Your Review", statuses: ["SoW Awaiting Approval", "Board Review"] },
  { id: "completed", label: "Assessment Completed", statuses: ["Submitted Assessment", "SoW & Project", "SoW requested", "Rejected", "Complete"] },
];

export const getIpeStatusGroup = (id: string): IpeStatusGroup | undefined =>
  IPE_STATUS_GROUPS.find((g) => g.id === id);

// Statuses that require the advisor's attention (everything except the
// terminal/passive "completed" group). Drives the dashboard's focus table.
export const IPE_FOCUS_STATUSES = new Set<string>(
  IPE_STATUS_GROUPS.filter((g) => g.id !== "completed").flatMap((g) => g.statuses),
);

// Resolve a status-filter value to the list of matching statuses, or null for
// "All". Accepts "All", a "group:<id>" key, or an exact status string.
export const resolveStatusFilter = (value: string): string[] | null => {
  if (!value || value === "All") return null;
  if (value.startsWith("group:")) {
    return getIpeStatusGroup(value.slice("group:".length))?.statuses ?? null;
  }
  return [value];
};

// Friendly label for a status-filter value (for filter chips).
export const statusFilterLabel = (value: string): string => {
  if (value.startsWith("group:")) {
    return getIpeStatusGroup(value.slice("group:".length))?.label ?? value;
  }
  return value;
};
